import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://dssbackend.recnet.in/api';

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const requestUrl = error.config?.url || ''; const isAuthRequest = requestUrl.includes('/admin/auth/');

    if (status === 401 && !isAuthRequest) {
      clearAuthToken();
      window.location.assign('/login');
    }

    return Promise.reject(error);
  }
);


export const getFileUrl = (path?: string) => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  // strip any trailing /api from API_URL to get base
  const base = API_URL.replace(/\/api.*$/, '');
  return `${base}${path}`;
};

export const adminLogin = async (email: string, password: string) => {
  const response = await api.post('/admin/auth/login', { email, password });
  if (response.data.token) {
    localStorage.setItem('adminToken', response.data.token);
  }
  return response.data;
};

export const adminRegister = async (name: string, email: string, password: string) => {
  const response = await api.post('/admin/auth/register', { name, email, password });
  if (response.data.token) {
    localStorage.setItem('adminToken', response.data.token);
  }
  return response.data;
};

export const getAuthToken = () => {
  return localStorage.getItem('adminToken');
};

export const clearAuthToken = () => {
  localStorage.removeItem('adminToken');
};

export const getAuthHeaders = () => {
  const token = getAuthToken();
  return {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  };
};

// Book APIs
export const getAllBooks = async () => {
  const response = await api.get('/admin/books', getAuthHeaders());
  const books = response.data || [];
  return books.map((b: any) => ({
    ...b,
    mainImage: getFileUrl(b.mainImage),
    additionalImages: Array.isArray(b.additionalImages)
      ? b.additionalImages.map((p: string) => getFileUrl(p))
      : b.additionalImages,
  }));
};

export const getBookById = async (id: string) => {
  const response = await api.get(`/admin/books/${id}`, getAuthHeaders());
  // API returns { book, documents }
  const book = response.data.book || response.data;
  const serverDocs = response.data.documents || [];

  // Map server documents to frontend shape used by BookForm
  const mappedDocs = serverDocs.map((d: any) => ({
    id: d._id || d.id,
    type: d.documentType || d.type,
    title: d.title,
    fileUrl: getFileUrl(d.fileUrl || (d.fileName ? `/uploads/documents/${d.fileName}` : '')),
  }));

  // Normalize mainImage and additionalImages to absolute URLs
  if (book.mainImage) {
    book.mainImage = getFileUrl(book.mainImage);
  }
  if (book.additionalImages && Array.isArray(book.additionalImages)) {
    book.additionalImages = book.additionalImages.map((p: string) => getFileUrl(p));
  }

  return { ...book, documents: book.documents && book.documents.length > 0 ? book.documents : mappedDocs };
};

export const createBook = async (bookData: any) => {
  const headers = getAuthHeaders().headers;
  const response = await api.post('/admin/books', bookData, {
    headers: {
      ...headers,
      'Content-Type': bookData instanceof FormData ? 'multipart/form-data' : 'application/json',
    },
  });
  return response.data;
};

export const updateBook = async (id: string, bookData: any) => {
  const headers = getAuthHeaders().headers;
  const response = await api.put(`/admin/books/${id}`, bookData, {
    headers: {
      ...headers,
      'Content-Type': bookData instanceof FormData ? 'multipart/form-data' : 'application/json',
    },
  });
  return response.data;
};

export const deleteBook = async (id: string) => {
  const response = await api.delete(`/admin/books/${id}`, getAuthHeaders());
  return response.data;
};

export const uploadDocument = async (bookId: string, formData: FormData) => {
  const headers = getAuthHeaders().headers;
  const response = await api.post(
    '/admin/books/document/upload',
    formData,
    { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
};

export const getBookDocuments = async (bookId: string) => {
  const response = await api.get(
    `/admin/books/documents/${bookId}`,
    getAuthHeaders()
  );
  return response.data;
};

export const deleteDocument = async (documentId: string) => {
  const response = await api.delete(
    `/admin/books/document/${documentId}`,
    getAuthHeaders()
  );
  return response.data;
};

export const updateDocument = async (documentId: string, data: FormData | any) => {
  const headers = getAuthHeaders().headers;
  const response = await api.put(
    `/admin/books/document/${documentId}`,
    data,
    {
      headers: {
        ...headers,
        'Content-Type': data instanceof FormData ? 'multipart/form-data' : 'application/json',
      },
    }
  );
  return response.data;
};

// User APIs
export const getAllUsers = async () => {
  const response = await api.get('/admin/users', getAuthHeaders());
  return response.data;
};

export const getUserById = async (id: string) => {
  const response = await api.get(`/admin/users/${id}`, getAuthHeaders());
  return response.data;
};

export const createUser = async (userData: any) => {
  const response = await api.post('/admin/users', userData, getAuthHeaders());
  return response.data;
};

export const updateUser = async (id: string, userData: any) => {
  const response = await api.put(`/admin/users/${id}`, userData, getAuthHeaders());
  return response.data;
};

export const deleteUser = async (id: string) => {
  const response = await api.delete(`/admin/users/${id}`, getAuthHeaders());
  return response.data;
};

// Order APIs
export const getAllOrders = async () => {
  const response = await api.get('/admin/orders', getAuthHeaders());
  return response.data;
};

export const getOrderById = async (id: string) => {
  const response = await api.get(`/admin/orders/${id}`, getAuthHeaders());
  return response.data;
};

export const getUserOrders = async (userId: string) => {
  const response = await api.get(`/admin/orders/user/${userId}`, getAuthHeaders());
  return response.data;
};

export const createOrder = async (orderData: any) => {
  const response = await api.post('/admin/orders', orderData, getAuthHeaders());
  return response.data;
};

export const updateOrderStatus = async (id: string, status: string, note: string, description: string) => {
  const response = await api.put(
    `/admin/orders/${id}/status`,
    { status, note, description },
    getAuthHeaders()
  );
  return response.data;
};

export const emailOrderStatus = async (id: string) => {
  const response = await api.post(`/admin/orders/${id}/email-status`, {}, getAuthHeaders());
  return response.data;
};

export const updateOrderItems = async (id: string, items: any[]) => {
  const response = await api.put(`/admin/orders/${id}/items`, { items }, getAuthHeaders());
  return response.data;
};

export const getPendingOrderSummary = async () => {
  const response = await api.get('/admin/orders/summary/pending', getAuthHeaders());
  return response.data;
};

export const deleteOrder = async (id: string) => {
  const response = await api.delete(`/admin/orders/${id}`, getAuthHeaders());
  return response.data;
};

// Client APIs
export const getAllClients = async () => {
  const response = await api.get('/admin/clients', getAuthHeaders());
  return response.data;
};

export const getClientById = async (id: string) => {
  const response = await api.get(`/admin/clients/${id}`, getAuthHeaders());
  return response.data;
};

export const createClient = async (clientData: any) => {
  const response = await api.post('/admin/clients', clientData, getAuthHeaders());
  return response.data;
};

export const updateClient = async (id: string, clientData: any) => {
  const response = await api.put(
    `/admin/clients/${id}`,
    clientData,
    getAuthHeaders()
  );
  return response.data;
};

export const deleteClient = async (id: string) => {
  const response = await api.delete(`/admin/clients/${id}`, getAuthHeaders());
  return response.data;
};

// DocumentType APIs
export const getAllDocumentTypes = async () => {
  const response = await api.get('/admin/document-types', getAuthHeaders());
  return response.data;
};

export const getDocumentTypeById = async (id: string) => {
  const response = await api.get(`/admin/document-types/${id}`, getAuthHeaders());
  return response.data;
};

export const createDocumentType = async (data: any) => {
  const response = await api.post('/admin/document-types', data, getAuthHeaders());
  return response.data;
};

export const updateDocumentType = async (id: string, data: any) => {
  const response = await api.put(`/admin/document-types/${id}`, data, getAuthHeaders());
  return response.data;
};

export const deleteDocumentType = async (id: string) => {
  const response = await api.delete(`/admin/document-types/${id}`, getAuthHeaders());
  return response.data;
};

// Subject APIs
export const getAllSubjects = async () => {
  const response = await api.get('/admin/subjects', getAuthHeaders());
  return response.data;
};

export const createSubject = async (data: any) => {
  const response = await api.post('/admin/subjects', data, getAuthHeaders());
  return response.data;
};

export const updateSubject = async (id: string, data: any) => {
  const response = await api.put(`/admin/subjects/${id}`, data, getAuthHeaders());
  return response.data;
};

export const deleteSubject = async (id: string) => {
  const response = await api.delete(`/admin/subjects/${id}`, getAuthHeaders());
  return response.data;
};

// Class APIs
export const getAllClasses = async () => {
  const response = await api.get('/admin/classes', getAuthHeaders());
  return response.data;
};

export const createClassLevel = async (data: any) => {
  const response = await api.post('/admin/classes', data, getAuthHeaders());
  return response.data;
};

export const updateClassLevel = async (id: string, data: any) => {
  const response = await api.put(`/admin/classes/${id}`, data, getAuthHeaders());
  return response.data;
};

export const deleteClassLevel = async (id: string) => {
  const response = await api.delete(`/admin/classes/${id}`, getAuthHeaders());
  return response.data;
};
